'use client'

import { ReactNode, useMemo } from 'react'

import { capitalize } from 'lodash'

import { commands } from '~/components/command-menu/commands'
import { CommandKeyDecoration } from '~/components/command-menu/components/ShortcutDecoration'
import {
  CommandContextArgs,
  CommandProps,
} from '~/components/command-menu/types'
import { DialogSectionHeader } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'

export const ShortcutsDialog = () => {
  const sections = useMemo(() => buildSections(commands), [])

  return (
    <SplitsDialogContent
      id="shortcutCheatsheet"
      title="Keyboard shortcuts"
      variant="info"
    >
      <div className="space-y-6">
        {sections.map((section) => (
          <div key={section.key} className="space-y-3">
            <DialogSectionHeader>{section.title}</DialogSectionHeader>
            <div className="space-y-2">
              {section.items.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <div className="text-gray-800 dark:text-gray-100">
                    {typeof item.label === 'string'
                      ? item.label.replace('...', '')
                      : item.label}
                  </div>
                  <div className="flex flex-wrap justify-end gap-2 text-xs">
                    {item.shortcuts.map((shortcut) => (
                      <CommandKeyDecoration
                        key={`${item.key}-${shortcut}`}
                        shortcut={shortcut}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SplitsDialogContent>
  )
}

type CheatSheetItem = {
  key: string
  label: ReactNode
  shortcuts: string[]
}

type CheatSheetSection = {
  key: string
  title: string
  items: CheatSheetItem[]
}

const DEFAULT_CONTEXT: CommandContextArgs = {
  pathname: '',
  activeView: null,
  compliance: {
    isVerified: false,
    canAddRecipients: false,
  },
  devtoolsVisible: false,
}

const formatCategoryLabel = (category: string) => {
  if (!category) return 'General'
  return capitalize(category.replace(/_/g, ' '))
}

const buildSections = (allCommands: CommandProps[]): CheatSheetSection[] => {
  const order: string[] = []
  const grouped = new Map<string, CheatSheetItem[]>()

  allCommands.forEach((command, index) => {
    if (!command.shortcuts || command.shortcuts.length === 0) return

    const category = command.category ?? 'general'
    if (!grouped.has(category)) {
      grouped.set(category, [])
      order.push(category)
    }

    const label = command.label(DEFAULT_CONTEXT)
    grouped.get(category)!.push({
      key: command.id ?? `${category}-${index}`,
      label,
      shortcuts: command.shortcuts,
    })
  })

  return order.map((category) => ({
    key: category,
    title: formatCategoryLabel(category),
    items: grouped.get(category) ?? [],
  }))
}
